import { useEffect, useMemo, useRef, useState } from 'react';
import type { DocumentRosterParticipant } from '../realtime/documentCollaborationTypes';
import {
  createPatientTrackingClient,
  type PatientTrackingClient
} from './createPatientTrackingClient';
import type {
  PatientRosterParticipant,
  PatientTrackingRowChange
} from './patientTrackingTypes';

export interface UseListCollaborationOptions {
  boardKey: string;
  enabled?: boolean;
  onRowUpserted?: (change: PatientTrackingRowChange) => void;
  onRowRemoved?: (change: PatientTrackingRowChange) => void;
}

export interface ListCollaboration {
  connected: boolean;
  roster: PatientRosterParticipant[];
  /** Roster mapped to the shared ActiveUsersBar shape. */
  activeBarRoster: DocumentRosterParticipant[];
  /** rowId -> participant currently editing that row (form-popup lock). */
  rowEditors: Map<string, PatientRosterParticipant>;
  beginEditRow: (rowId: string) => void;
  endEditRow: () => void;
  setListEditing: (listEditing: boolean) => void;
}

/**
 * Subscribes to a tracker board's presence + row-change stream. Mirrors useDocumentPresence: one
 * connection per mounted board, roster state, and helpers to publish this user's edit intent.
 */
export function useListCollaboration(options: UseListCollaborationOptions): ListCollaboration {
  const { boardKey, enabled = true } = options;

  const [connected, setConnected] = useState(false);
  const [roster, setRoster] = useState<PatientRosterParticipant[]>([]);

  const upsertRef = useRef(options.onRowUpserted);
  const removeRef = useRef(options.onRowRemoved);
  upsertRef.current = options.onRowUpserted;
  removeRef.current = options.onRowRemoved;

  const clientRef = useRef<PatientTrackingClient | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const client = createPatientTrackingClient({
      boardKey,
      onRosterChanged: setRoster,
      onRowUpserted: (change) => upsertRef.current?.(change),
      onRowRemoved: (change) => removeRef.current?.(change),
      onConnectedChange: setConnected
    });
    clientRef.current = client;
    void client.start();

    return () => {
      clientRef.current = null;
      void client.stop();
      setRoster([]);
      setConnected(false);
    };
  }, [boardKey, enabled]);

  const rowEditors = useMemo(() => {
    const map = new Map<string, PatientRosterParticipant>();
    for (const p of roster) {
      if (p.editingRowId) map.set(p.editingRowId, p);
    }
    return map;
  }, [roster]);

  const activeBarRoster = useMemo<DocumentRosterParticipant[]>(
    () =>
      roster.map((p) => ({
        userId: p.userId,
        displayName: p.displayName,
        upn: p.upn,
        hasPhoto: null,
        connections: p.connections,
        isEditing: p.listEditing || Boolean(p.editingRowId),
        isTyping: false,
        lastTypingActivityUtc: null
      })),
    [roster]
  );

  return {
    connected,
    roster,
    activeBarRoster,
    rowEditors,
    beginEditRow: (rowId: string) => clientRef.current?.beginEditRow(rowId),
    endEditRow: () => clientRef.current?.endEditRow(),
    setListEditing: (listEditing: boolean) => clientRef.current?.setListEditing(listEditing)
  };
}
